import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIntelReports } from '@/lib/intelSourceV2';
import { getFeatured } from '@/constants/partners';
import { shouldSkipAnimation } from '@/lib/prerender';

/**
 * SplitText — cinematic word-by-word reveal.
 *
 * Accessibility / SEO contract:
 *   • Words are separated by REAL whitespace text nodes (not margin), so the
 *     DOM text reads "We Align Business Strategy" — not "WeAlignBusinessStrategy" —
 *     for screen readers, search engines and AI crawlers.
 *   • During prerender (static HTML snapshot) or with prefers-reduced-motion the
 *     words render fully visible with no transform, so no-JS clients never see
 *     words stuck at opacity:0 / clipped.
 */
const SplitText = ({ children, className, delay = 0 }) => {
  const words = children.split(" ");
  const skip = shouldSkipAnimation();
  return (
    <span className={className}>
      {words.map((word, i) => (
        <React.Fragment key={i}>
          <span className="inline-block overflow-hidden align-bottom">
            <motion.span
              initial={skip ? false : { y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                duration: 0.5,
                delay: delay + i * 0.1,
                ease: [0.33, 1, 0.68, 1] // Cinematic easing
              }}
              className="inline-block font-title"
              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
            >
              {word}
            </motion.span>
          </span>
          {i < words.length - 1 ? ' ' : null}
        </React.Fragment>
      ))}
    </span>
  );
};

// Neural Flow Canvas — canvas-based particle mesh that reacts to mouse movement.
// Replaces the static SVG parallax for a "live intelligence" feel.
const NeuralCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    const mouse = { x: -9999, y: -9999 };

    const setSize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    setSize();

    const N = 72;
    const mkPt = () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.38,
      vy: (Math.random() - 0.5) * 0.38,
      r: Math.random() * 1.2 + 0.4,
    });
    let pts = Array.from({ length: N }, mkPt);

    const LINK = 130;

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const p of pts) {
        // Mouse repulsion field
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d = Math.hypot(dx, dy);
        if (d < 110 && d > 0) {
          const f = ((110 - d) / 110) * 0.45;
          p.vx += (dx / d) * f;
          p.vy += (dy / d) * f;
        }
        p.vx *= 0.97;
        p.vy *= 0.97;
        p.x += p.vx;
        p.y += p.vy;
        // Wrap edges (toroidal)
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        // Node dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(74,144,226,0.75)';
        ctx.fill();
      }

      // Neural connections
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < LINK) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(74,144,226,${(1 - d / LINK) * 0.18})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      raf = requestAnimationFrame(tick);
    };

    tick();

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onResize = () => {
      setSize();
      pts = Array.from({ length: N }, mkPt);
    };

    window.addEventListener('mousemove', onMouse);
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full z-[1] pointer-events-none"
      style={{ opacity: 0.38 }}
    />
  );
};


// ── Ghost Panels ─────────────────────────────────────────────────────────────
// Each panel triggers on DIRECT hover (individual, not section-wide).
// GhostPanel passes `hovered` to children via render-prop so content can
// animate live: progress bars fill, items stagger in, dots pulse, etc.
// z-[12] → always above the z-10 content container.

const GhostPanel = ({ children, style, width = 228, accentColor = '#4A90E2' }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      className="absolute hidden md:block cursor-default select-none"
      style={{ width, ...style, zIndex: hovered ? 20 : 12 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      initial={{ opacity: 0.09, y: 6 }}
      animate={{ opacity: hovered ? 1 : 0.09, y: hovered ? 0 : 6, scale: hovered ? 1.03 : 1 }}
      transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <div
        className="bg-[#0c0f14]/92 backdrop-blur-md rounded-[2px] p-4 transition-shadow duration-300"
        style={{
          border: `1px solid ${hovered ? accentColor + '65' : 'rgba(255,255,255,0.13)'}`,
          boxShadow: hovered
            ? `0 0 22px ${accentColor}28, 0 0 6px ${accentColor}18, 0 8px 32px rgba(0,0,0,0.45)`
            : '0 2px 8px rgba(0,0,0,0.22)',
        }}
      >
        {/* Render-prop: child can be a fn(hovered) for live animations */}
        {typeof children === 'function' ? children(hovered) : children}
      </div>
    </motion.div>
  );
};

// ── Panel 1 — Enterprise Process Flow (top-right) ──
// Items stagger in one-by-one when panel is hovered
const ProcessFlowPanel = () => (
  <GhostPanel style={{ top: '4%', right: '5%' }} width={238} accentColor="#4A90E2">
    {(hovered) => (
      <>
        <div className="flex items-center gap-2 mb-3 border-b border-white/[0.1] pb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4A90E2] animate-pulse" />
          <span className="font-mono text-[8px] text-[#4A90E2] uppercase tracking-[0.18em]">PROCESS: ENTERPRISE_AUDIT</span>
        </div>
        <div className="space-y-2.5">
          {[
            { n: '01', label: 'ASSESS LEGACY STACK', done: true },
            { n: '02', label: 'ARCHITECT COMPLIANCE', done: true },
            { n: '03', label: 'AUTOMATE WORKFLOWS', done: false },
            { n: '04', label: 'GOVERN & REPORT', done: false },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0.25, x: -6 }}
              animate={{ opacity: hovered ? 1 : 0.25, x: hovered ? 0 : -6 }}
              transition={{ duration: 0.3, delay: hovered ? i * 0.09 : 0 }}
              className="flex items-center gap-2.5"
            >
              <span className="w-5 h-5 rounded-[1px] border flex-shrink-0 flex items-center justify-center font-mono text-[7px]"
                style={{ borderColor: s.done ? '#4A90E2' : 'rgba(255,255,255,0.18)', color: s.done ? '#4A90E2' : 'rgba(255,255,255,0.28)', background: s.done ? 'rgba(74,144,226,0.12)' : 'transparent' }}
              >{s.n}</span>
              <span className="font-mono text-[8px] tracking-wider flex-1"
                style={{ color: s.done ? 'rgba(200,215,225,0.9)' : 'rgba(140,155,165,0.5)' }}>
                {s.label}
              </span>
              {i < 3 && (
                <svg width="7" height="9" viewBox="0 0 7 9" fill="none">
                  <path d="M3.5 0v7M1 5l2.5 3L6 5" stroke="rgba(74,144,226,0.3)" strokeWidth="1"/>
                </svg>
              )}
            </motion.div>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-white/[0.06] font-mono text-[7px] text-gray-600 uppercase tracking-widest">
          REF: OPS_PROC_2026 // DD_CONSULTING
        </div>
      </>
    )}
  </GhostPanel>
);

// ── Panel 2 — AI Maturity Assessment (top-LEFT — moved above the briefing card) ──
// Progress bars fill from 0 → target % on hover with staggered delay
const AIMaturityPanel = () => (
  <GhostPanel style={{ top: '4%', right: '28%' }} width={218} accentColor="#4A90E2">
    {(hovered) => (
      <>
        <div className="flex items-center gap-2 mb-3 border-b border-white/[0.1] pb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4A90E2]" />
          <span className="font-mono text-[8px] text-[#4A90E2] uppercase tracking-[0.18em]">AI MATURITY ASSESSMENT</span>
        </div>
        <div className="space-y-2.5">
          {[
            { label: 'L1  ISOLATED',   pct: 100, color: '#4A90E2' },
            { label: 'L2  CONNECTED',  pct: 100, color: '#4A90E2' },
            { label: 'L3  INTEGRATED', pct: 65,  color: '#9e591e' },
            { label: 'L4  OPTIMISED',  pct: 38,  color: '#9e591e' },
            { label: 'L5  AUTONOMOUS', pct: 18,  color: 'rgba(150,162,172,0.5)' },
          ].map((item, i) => (
            <div key={i}>
              <div className="flex justify-between mb-1">
                <span className="font-mono text-[7px] text-gray-400">{item.label}</span>
                <motion.span
                  className="font-mono text-[7px]"
                  style={{ color: item.color }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: hovered ? 1 : 0 }}
                  transition={{ duration: 0.3, delay: hovered ? i * 0.12 + 0.3 : 0 }}
                >{item.pct}%</motion.span>
              </div>
              {/* Track */}
              <div className="h-[3px] bg-white/[0.08] rounded-full overflow-hidden">
                {/* Bar fills from left on hover */}
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: item.color, originX: 0 }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: hovered ? item.pct / 100 : 0 }}
                  transition={{ duration: 0.6, delay: hovered ? i * 0.12 : 0, ease: [0.25, 0.46, 0.45, 0.94] }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-white/[0.06] flex items-center justify-between">
          <span className="font-mono text-[7px] text-gray-600 uppercase tracking-widest">CURRENT LEVEL</span>
          <motion.span
            className="font-mono text-[9px] font-bold text-[#4A90E2]"
            initial={{ opacity: 0 }}
            animate={{ opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
          >2.8 / 5.0</motion.span>
        </div>
      </>
    )}
  </GhostPanel>
);

// ── Panel 3 — Multi-Agent AI Pipeline (RIGHT-SIDE of briefing card, above title) ──
// Active agent dots pulse continuously when hovered
const MultiAgentPanel = () => (
  <GhostPanel style={{ top: '34%', right: '3%' }} width={222} accentColor="#9e591e">
    {(hovered) => (
      <>
        <div className="flex items-center gap-2 mb-3 border-b border-white/[0.1] pb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#9e591e] animate-pulse" />
          <span className="font-mono text-[8px] text-[#9e591e] uppercase tracking-[0.18em]">MULTI_AGENT_PIPELINE</span>
        </div>
        <div className="space-y-2">
          {[
            { id: 'AGENT_01', role: 'DATA_INGEST',   status: 'ACTIVE',  active: true,  color: '#4A90E2' },
            { id: 'AGENT_02', role: 'REASON_ENGINE', status: 'ACTIVE',  active: true,  color: '#4A90E2' },
            { id: 'AGENT_03', role: 'ACTION_PROXY',  status: 'RUNNING', active: true,  color: '#9e591e' },
            { id: 'AGENT_04', role: 'VERIFY_GATE',   status: 'QUEUED',  active: false, color: 'rgba(150,160,170,0.45)' },
          ].map((a, i) => (
            <div key={i} className="flex items-center gap-2">
              <motion.span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: a.color }}
                animate={hovered && a.active ? {
                  scale: [1, 1.6, 1],
                  opacity: [1, 0.6, 1],
                  boxShadow: [`0 0 4px ${a.color}`, `0 0 10px ${a.color}`, `0 0 4px ${a.color}`],
                } : { scale: 1, opacity: 1 }}
                transition={hovered && a.active ? {
                  duration: 1.4,
                  delay: i * 0.3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                } : { duration: 0.2 }}
              />
              <span className="font-mono text-[7.5px] text-gray-400 w-16">{a.id}</span>
              <span className="font-mono text-[7px] text-gray-500 truncate flex-1">{a.role}</span>
              <span className="font-mono text-[6.5px] uppercase tracking-widest" style={{ color: a.color }}>{a.status}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-white/[0.06] font-mono text-[7px] text-[#9e591e]/55 uppercase tracking-widest">
          ORCHESTRATOR: ACTIVE // AGENTS: 4
        </div>
      </>
    )}
  </GhostPanel>
);

// ── Panel 4 — AI Agent Topology SVG (right-edge, lower) ──
// Node circles pulse on hover
const AgentTopologyPanel = () => (
  <GhostPanel style={{ top: '58%', right: '1%' }} width={206} accentColor="#9e591e">
    {(hovered) => (
      <>
        <div className="flex items-center gap-2 mb-2 border-b border-white/[0.1] pb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#9e591e] animate-pulse" />
          <span className="font-mono text-[8px] text-[#9e591e] uppercase tracking-[0.18em]">// AI_AGENT_TOPOLOGY</span>
        </div>
        <svg viewBox="0 0 168 90" className="w-full" fill="none">
          <line x1="22" y1="22" x2="84" y2="45" stroke="rgba(74,144,226,0.32)" strokeWidth="0.8" strokeDasharray="4 2"/>
          <line x1="146" y1="22" x2="84" y2="45" stroke="rgba(74,144,226,0.32)" strokeWidth="0.8" strokeDasharray="4 2"/>
          <line x1="22" y1="70" x2="84" y2="45" stroke="rgba(74,144,226,0.22)" strokeWidth="0.8" strokeDasharray="4 2"/>
          <line x1="146" y1="70" x2="84" y2="45" stroke="rgba(74,144,226,0.22)" strokeWidth="0.8" strokeDasharray="4 2"/>
          <line x1="84" y1="45" x2="160" y2="45" stroke="rgba(158,89,30,0.6)" strokeWidth="1.2"/>
          {/* Input nodes — pulse outward on hover */}
          {[[22,22],[146,22],[22,70],[146,70]].map(([cx,cy],i) => (
            <motion.circle key={i} cx={cx} cy={cy} r="6"
              fill="rgba(74,144,226,0.8)"
              style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
              // animate scale, not the `r` attribute (motion appends "px" → invalid SVG length errors)
              animate={hovered ? { scale: [1, 1.33, 1], opacity: [0.8, 1, 0.8] } : { scale: 1 }}
              transition={hovered ? { duration: 1.2, delay: i * 0.25, repeat: Infinity, ease: 'easeInOut' } : {}}
            />
          ))}
          {/* Coordinator hub — glows on hover */}
          <motion.circle cx="84" cy="45" r="11"
            fill="rgba(158,89,30,0.8)"
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            animate={hovered ? { scale: [1, 1.18, 1] } : { scale: 1 }}
            transition={hovered ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : {}}
          />
          <circle cx="84" cy="45" r="6" fill="rgba(158,89,30,0.35)" stroke="rgba(158,89,30,0.8)" strokeWidth="1"/>
          <circle cx="162" cy="45" r="5" fill="rgba(74,144,226,0.55)" stroke="rgba(74,144,226,0.4)" strokeWidth="1"/>
          <text x="14" y="13" fill="rgba(175,188,200,0.52)" fontSize="5.5" fontFamily="monospace">DATA</text>
          <text x="137" y="13" fill="rgba(175,188,200,0.52)" fontSize="5.5" fontFamily="monospace">DOCS</text>
          <text x="77" y="58" fill="rgba(215,195,170,0.82)" fontSize="5" fontFamily="monospace">COORD</text>
          <text x="153" y="43" fill="rgba(175,188,200,0.5)" fontSize="5" fontFamily="monospace">OUT</text>
        </svg>
        <div className="font-mono text-[7px] text-gray-600 uppercase tracking-widest mt-1">NODES: 5 // STATUS: ACTIVE</div>
      </>
    )}
  </GhostPanel>
);

// ── Panel 5 — Document Enhancement Checklist (lower-left of card) ──
// Checkmarks pop in with stagger on hover
const DocumentEnhancePanel = () => (
  <GhostPanel style={{ bottom: '14%', right: '25%' }} width={210} accentColor="#22c55e">
    {(hovered) => (
      <>
        <div className="flex items-center gap-2 mb-3 border-b border-white/[0.1] pb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          <span className="font-mono text-[8px] text-green-400 uppercase tracking-[0.18em]">DOCUMENT: ENHANCED</span>
          <span className="ml-auto font-mono text-[7px] text-green-500/60 border border-green-500/25 px-1.5 py-0.5">PASS</span>
        </div>
        <div className="space-y-2">
          {[
            { label: 'APP_COMPLIANT',        ok: true },
            { label: 'APRA_CPS_234',         ok: true },
            { label: 'AI_INDEXED',           ok: true },
            { label: 'SOVEREIGNTY_VERIFIED', ok: false, pending: true },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <motion.span
                className={`text-[10px] w-3 flex-shrink-0 ${item.ok ? 'text-green-400' : item.pending ? 'text-yellow-500' : 'text-gray-600'}`}
                initial={{ scale: 0.4, opacity: 0.3 }}
                animate={{ scale: hovered ? 1 : 0.4, opacity: hovered ? 1 : 0.3 }}
                transition={{ duration: 0.25, delay: hovered ? i * 0.1 : 0, type: 'spring', stiffness: 300 }}
              >{item.ok ? '✓' : '◎'}</motion.span>
              <span className="font-mono text-[7.5px] tracking-wider"
                style={{ color: item.ok ? 'rgba(185,205,195,0.85)' : 'rgba(175,155,95,0.7)' }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-white/[0.06] flex items-center justify-between">
          <span className="font-mono text-[7px] text-gray-600 uppercase tracking-widest">POLICY_BRIEF</span>
          <span className="font-mono text-[7px] text-green-500/50 uppercase">3/4 OK</span>
        </div>
      </>
    )}
  </GhostPanel>
);
const TrustBar = () => {
  const featured = getFeatured();

  return (
    <div className="w-full bg-[#1F232B] py-4 border-t-2 border-[#9e591e] z-20 relative mt-auto">
      <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <span className="font-heading text-[10px] text-gray-400 uppercase tracking-widest">
          Partners & Alliances
        </span>
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
          {featured.map((p) => {
            // Hero TrustBar uses two card styles to preserve the existing
            // visual language: inline-svg partners render as glyph + bold
            // label (AWS/Google pattern); image partners render in a
            // strategic-card treatment (white tile + label).
            const commonWrapperProps = {
              className:
                "group opacity-60 hover:opacity-100 transition-opacity duration-300 cursor-pointer flex items-center gap-2",
              title: p.description,
              "aria-label": p.alt,
              "data-testid": `trustbar-partner-${p.id}`,
            };

            if (p.renderMode === "inline-svg" && p.svgId === "aws") {
              // Internal link → semantic <Link> so crawlers can discover it.
              if (p.internalLink) {
                return (
                  <Link key={p.id} to={p.internalLink} {...commonWrapperProps}>
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24" role="img" xmlns="http://www.w3.org/2000/svg"><title>Amazon Web Services</title><path d="M11.128 15.657c.563 0 1.096-.13 1.585-.386l.995 1.55c-.805.545-1.79.865-2.853.865-2.894 0-5.176-2.073-5.176-4.992 0-2.853 2.193-4.945 4.965-4.945 2.65 0 4.675 1.95 4.675 4.665 0 .34-.034.67-.097.986l-2.077-.282c.036-.21.055-.43.055-.65 0-1.573-1.125-2.73-2.585-2.73-1.523 0-2.76 1.185-2.76 2.94 0 1.765 1.252 2.978 2.793 2.978h.48zm6.54-5.226v5.91h-1.637v-.793c-.633.61-1.46.97-2.383.97-1.895 0-3.08-1.226-3.08-3.04 0-1.745 1.19-2.925 3.125-2.925.86 0 1.633.298 2.228.797v-.125c0-1.077-.866-1.92-2.14-1.92-1.022 0-1.88.46-2.28 1.155l-1.558-.9c.702-1.15 2.143-1.928 3.868-1.928 2.303 0 3.857 1.48 3.857 3.797zm-1.666 3.13v-1.63c-.49-.405-1.066-.62-1.716-.62-1.055 0-1.61.62-1.61 1.45 0 .82.596 1.39 1.575 1.39.73 0 1.34-.236 1.75-.59z"/></svg>
                    <span className="font-heading font-bold text-white text-lg">{p.name}</span>
                  </Link>
                );
              }
              return (
                <div key={p.id} {...commonWrapperProps}>
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24" role="img" xmlns="http://www.w3.org/2000/svg"><title>Amazon Web Services</title><path d="M11.128 15.657c.563 0 1.096-.13 1.585-.386l.995 1.55c-.805.545-1.79.865-2.853.865-2.894 0-5.176-2.073-5.176-4.992 0-2.853 2.193-4.945 4.965-4.945 2.65 0 4.675 1.95 4.675 4.665 0 .34-.034.67-.097.986l-2.077-.282c.036-.21.055-.43.055-.65 0-1.573-1.125-2.73-2.585-2.73-1.523 0-2.76 1.185-2.76 2.94 0 1.765 1.252 2.978 2.793 2.978h.48zm6.54-5.226v5.91h-1.637v-.793c-.633.61-1.46.97-2.383.97-1.895 0-3.08-1.226-3.08-3.04 0-1.745 1.19-2.925 3.125-2.925.86 0 1.633.298 2.228.797v-.125c0-1.077-.866-1.92-2.14-1.92-1.022 0-1.88.46-2.28 1.155l-1.558-.9c.702-1.15 2.143-1.928 3.868-1.928 2.303 0 3.857 1.48 3.857 3.797zm-1.666 3.13v-1.63c-.49-.405-1.066-.62-1.716-.62-1.055 0-1.61.62-1.61 1.45 0 .82.596 1.39 1.575 1.39.73 0 1.34-.236 1.75-.59z"/></svg>
                  <span className="font-heading font-bold text-white text-lg">{p.name}</span>
                </div>
              );
            }

            if (p.renderMode === "inline-svg" && p.svgId === "google-cloud") {
              return (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...commonWrapperProps}
                >
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24" role="img" xmlns="http://www.w3.org/2000/svg"><title>Google Cloud</title><path d="M12.915 13.974v-2.82h-3.39v-.006c-1.397.066-2.568.966-2.955 2.296h6.345zM22.023 11.2h-3.264c-.208-2.618-2.18-4.73-4.663-5.187V2.516C18.602 3.104 22.022 6.74 22.022 11.2zM2.81 12.87c.21 2.585 2.15 4.673 4.604 5.15v3.465C3.33 20.897-.002 17.31-.002 12.87c0-.022 0-.045.002-.067h2.81zm9.19 8.614v-3.468c-1.838-.173-3.37-1.428-3.95-3.15H1.67c.725 3.766 4.02 6.618 7.94 6.618 1.11 0 2.17-.215 3.16-.606l-2.15-2.836c-.167.06-.34.11-.518.143zM15.46 3.778l2.13-2.81C16.602.582 15.547.37 14.444.37c-3.93 0-7.23 2.864-7.95 6.638h6.375c.57-1.703 2.083-2.94 3.904-3.12l-1.314-.11z"/></svg>
                  <span className="font-heading font-bold text-white text-lg">{p.name}</span>
                </a>
              );
            }

            // badge partner → white tile, larger display for certification badges
            if (p.renderMode === 'badge') {
              const BadgeTag = p.internalLink ? Link : 'a';
              const badgeProps = p.internalLink
                ? { to: p.internalLink }
                : { href: p.url, target: '_blank', rel: 'noopener noreferrer' };
              return (
                <BadgeTag
                  key={p.id}
                  {...badgeProps}
                  className="group opacity-80 hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-1"
                  title={p.description}
                  aria-label={p.alt}
                  data-testid={`trustbar-partner-${p.id}`}
                >
                  <div className="flex items-center justify-center px-3 py-1.5 border border-[#FF9900]/50 rounded-[2px] bg-white hover:border-[#FF9900] transition-colors shadow-sm">
                    <img
                      src={p.logo}
                      alt={p.alt}
                      className="h-12 w-auto max-w-[140px] object-contain"
                      loading="eager"
                    />
                  </div>
                </BadgeTag>
              );
            }

            // image partner → clean tile, dark bg so white logos (e.g. FX1) render properly
            return (
              <a
                key={p.id}
                href={p.url || '#'}
                target={p.url ? "_blank" : "_self"}
                rel="noopener noreferrer"
                className="group opacity-70 hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-1"
                title={p.description}
                aria-label={p.alt}
                data-testid={`trustbar-partner-${p.id}`}
              >
                <div className="flex items-center gap-2 px-3 py-2 border border-white/15 rounded-[2px] bg-[#1A1D21] hover:border-white/30 transition-colors">
                  <img
                    src={p.logoWhite || p.logo}
                    alt={p.alt}
                    className="h-6 w-auto max-w-[96px] object-contain"
                    loading="lazy"
                  />
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function Hero() {
  // Read intelligence reports from the same source the Intelligence Hub
  // uses: Google Sheets (with cached + legacy-bundled fallback). This keeps
  // the Hero's "LATEST INTELLIGENCE" card in sync with whatever editors
  // publish in the sheet without redeploying.
  const { reports } = useIntelReports();

  const latestIntel = useMemo(() => {
    // "LATEST INTELLIGENCE" surfaces only real case studies.
    //
    // Classification priority (Type column authoritative when set):
    //   1. sheet's `Type` column — respects deliberate editor re-classification
    //      (case_study / briefing / blog / etc). Normalised in intelSourceV2.js.
    //   2. ref_id prefix fallback — legacy rows without a Type value:
    //        CASE_*         → case study
    //        BLOG_/OPS_/METH_ → briefing (excluded)
    // Also excludes:
    //   • pinned/evergreen entries
    const eligible = (reports || []).filter((r) => {
      if (!r) return false;
      if (r.pinned) return false;

      // Priority 1: derived `r.type` reflects the sheet's Type column.
      if (r.type === 'case_study') return true;
      if (r.type === 'briefing') return false;

      // Priority 2 (fallback): ref_id prefix convention.
      const ref = r.ref_id || '';
      if (ref.startsWith('CASE_')) return true;
      if (ref.startsWith('BLOG_') || ref.startsWith('OPS_') || ref.startsWith('METH_')) return false;
      return false; // Unknown → exclude to be safe
    });
    eligible.sort((a, b) => new Date(b.date) - new Date(a.date));
    // Fallback if no eligible entries (defensive — keeps the card populated).
    return eligible[0]
      || (reports || []).find((r) => r && r.type === 'case_study')
      || (reports || [])[0];
  }, [reports]);

  return (
    <section
      className="relative bg-[#2D323E] min-h-[82vh] flex flex-col items-center overflow-hidden pt-12 md:pt-16 border-b-2 border-border"
    >
        {/* Grid Line Overlay */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" className="opacity-[0.07]">
            <defs>
              <pattern id="hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,1)" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid)" />
          </svg>
        </div>

        {/* Neural Flow Canvas */}
        <NeuralCanvas />

        {/* Ghost Panels — individually hoverable, z-[12] keeps them above the card */}
        <ProcessFlowPanel />
        <MultiAgentPanel />
        <AgentTopologyPanel />
        <AIMaturityPanel />
        <DocumentEnhancePanel />

        <div className="container mx-auto px-4 relative z-10 flex flex-col justify-center flex-grow py-6 md:pt-10 md:pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            
            {/* Left Side: Existing Content */}
            <div className="flex flex-col justify-center">
              <span className="font-mono text-[#4A90E2] tracking-widest text-sm mb-4 block">
                TECHNICAL INTELLIGENCE
              </span>
              {/* Cinematic Headline */}
              <h1 className="mb-6">
                  <SplitText 
                      className="font-title text-4xl md:text-[64px] leading-[0.95] uppercase tracking-[1px] text-white block"
                      delay={0}
                  >
                      We Align Business Strategy
                  </SplitText>
                  <SplitText 
                      className="font-title text-4xl md:text-[64px] leading-[0.95] uppercase tracking-[1px] text-white block"
                      delay={0.18}
                  >
                      With Technical
                  </SplitText>
                  <SplitText 
                      className="font-title text-4xl md:text-[64px] leading-[0.95] uppercase tracking-[1px] text-[#4A90E2] block"
                      delay={0.34}
                  >
                      Reality
                  </SplitText>
              </h1>

              {/* Subhead */}
              <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.8 }}
                  className="text-lg md:text-2xl text-[#9e591e] mb-8 max-w-2xl font-heading font-semibold"
              >
                We don&apos;t just build software. We fix the operational bottlenecks holding you back, then engineer the technology to scale.
              </motion.p>

              {/* Financial Authority Trust Signal */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.65, duration: 0.8 }}
                className="flex items-center gap-2 mb-12 text-gray-400 font-mono text-xs uppercase tracking-wider"
              >
                <ShieldCheck className="w-4 h-4 text-[#4A90E2]" />
                Trusted with multi-million-dollar technical asset portfolios.
              </motion.div>

              {/* Action Center */}
              <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.75, duration: 0.8 }}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-8 mb-8 md:mb-0"
              >
                {/* Button Style A */}
                <Button 
                  asChild
                  size="lg" 
                  className="btn-hero-glow bg-[#4A90E2] text-white hover:bg-[#3a7bc8] rounded-[2px] border-2 border-[#4A90E2] shadow-block hover:-translate-y-[2px] transition-all duration-200 ease-steps-3 text-lg px-8 h-14 uppercase font-heading tracking-wide w-full sm:w-auto"
                >
                  <Link to="/contact">Book Strategy Audit</Link>
                </Button>

                {/* Secondary Link with Animated Arrow */}
                <div className="flex flex-col items-start sm:items-center w-full sm:w-auto">
                  <a 
                    href="/intelligence" 
                    className="group flex items-center gap-2 text-white font-heading font-semibold uppercase tracking-wide text-sm border-b-2 border-white pb-1 hover:text-white/80 hover:border-white/80 transition-colors"
                  >
                    View Intelligence
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-[5px]" />
                  </a>
                  <span className="text-[10px] text-gray-500 block mt-1 tracking-wider uppercase opacity-80">Strategic intelligence briefings</span>
                </div>
              </motion.div>
            </div>

            {/* Right Side: Featured Intelligence Card */}
            {latestIntel && (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="flex items-center justify-center mt-8 md:mt-0"
            >
              <Link
                to={`/intelligence/${latestIntel.id}`}
                className="relative backdrop-blur-md bg-white/5 border border-white/10 rounded-[2px] p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:bg-white/10 hover:border-[#4A90E2]/50 transition-all duration-300 cursor-pointer group w-full max-w-md block no-underline"
                aria-label={`Read latest intelligence: ${latestIntel.headline || latestIntel.title}`}
              >
                {/* Glassmorphism Effect Enhanced */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#4A90E2]/5 to-[#9e591e]/5 rounded-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <span className="font-mono text-[#4A90E2] text-xs tracking-widest uppercase mb-4 block">
                    // LATEST INTELLIGENCE
                  </span>
                  
                  <h3 className="font-title text-3xl md:text-4xl text-white uppercase mb-6 leading-tight">
                    {latestIntel.headline || latestIntel.title}
                  </h3>
                  
                  <div className="space-y-4 mb-6">
                    {/* Sheet v2 stores metrics as top-level "LABEL: VALUE" strings */}
                    {latestIntel.metric1 && (
                      <div className="flex items-center justify-between border-b border-white/10 pb-3">
                        <span className="font-mono text-xs text-gray-400 uppercase tracking-wider">
                          {latestIntel.metric1.split(':')[0]}
                        </span>
                        <span className="font-heading font-bold text-2xl text-[#4A90E2]">
                          {latestIntel.metric1.split(':')[1]?.trim() || latestIntel.metric1}
                        </span>
                      </div>
                    )}
                    {latestIntel.metric2 && (
                      <div className="flex items-center justify-between border-b border-white/10 pb-3">
                        <span className="font-mono text-xs text-gray-400 uppercase tracking-wider">
                          {latestIntel.metric2.split(':')[0]}
                        </span>
                        <span className="font-heading font-bold text-2xl text-[#9e591e]">
                          {latestIntel.metric2.split(':')[1]?.trim() || latestIntel.metric2}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    variant="outline" 
                    className="w-full border-white/30 text-white hover:bg-white/10 hover:border-[#4A90E2] rounded-[2px] uppercase font-heading tracking-wide"
                  >
                    Read Briefing
                  </Button>
                </div>
              </Link>
            </motion.div>
            )}

          </div>
        </div>

        {/* Sticky Trust Bar */}
        <TrustBar />
      </section>
  );
}
